import styled from "@emotion/styled";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, Tooltip } from "react-leaflet";
import ParkkiTheme from "themes/ParkkiTheme";
import MapConfig from "../services/map/MapConfig";

const ALTERNATING_INTERVAL_MS = 2500;

const WIDTH_PER_CHAR = 12;
type StyledTooltipProps = {
  maxDigits?: number;
  color: string;
  selected: boolean;
  labelLength?: { length: number; nbSymbols: number };
};

const StyledTooltip = styled(Tooltip)<StyledTooltipProps>`
  &.leaflet-tooltip.leaflet-tooltip-center {
    z-index: 1;
    cursor: grab;
    min-width: 24px;
    min-height: 30px;
    width: ${(props) =>
      props.maxDigits && props.maxDigits > 0
        ? `${
            props.maxDigits * WIDTH_PER_CHAR +
            (props.labelLength?.nbSymbols || 0) * (WIDTH_PER_CHAR / 1.5)
          }px`
        : "auto"};
    display: flex;
    align-items: flex-start;
    justify-content: center;
    font-family: "Led Counter";
    font-size: 20px;
    padding-top: 6px;
    padding-bottom: 2px;
    padding-right: 10px;
    padding-left: 10px;
    margin: 0px;
    border: ${(props) =>
      `4px solid ${props.selected ? ParkkiTheme.colors.turquoise : "black"}`};
    color: ${(props) => props.color};
    background-color: #2d2d39;

    & > svg {
      margin-right: ${ParkkiTheme.spacings.teeny};
    }

    & > #title {
      border-top-right-radius: 3px;
      border-top-left-radius: 3px;
      width: calc(100% - 10px);
      font-family: "Helvetica Neue";
      color: white;
      font-size: 12px;
      padding-right: 5px;
      padding-left: 5px;
      position: absolute;
      top: -18px;
      left: -4px;
      background-color: #2d2d39;
      text-overflow: ellipsis;
      overflow: hidden;
      border-left: ${(props) =>
        `4px solid ${props.selected ? ParkkiTheme.colors.turquoise : "black"}`};
      border-right: ${(props) =>
        `4px solid ${props.selected ? ParkkiTheme.colors.turquoise : "black"}`};
      border-top: ${(props) =>
        `4px solid ${props.selected ? ParkkiTheme.colors.turquoise : "black"}`};
    }
  }
`;

const LabelContainer = styled.span`
  width: 100%;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
`;

const StyledLabel = styled.span`
  display: flex;
`;

const AutoScrollingLabel = styled(StyledLabel)`
  display: flex;
  @keyframes translate-x-animation {
    from {
      -moz-transform: translateX(100%);
      -webkit-transform: translateX(100%);
      transform: translateX(100%);
    }
    to {
      -moz-transform: translateX(-100%);
      -webkit-transform: translateX(-100%);
      transform: translateX(-100%);
    }
  }

  /* for Firefox */
  @-moz-keyframes translate-x-animation {
    from {
      -moz-transform: translateX(100%);
    }
    to {
      -moz-transform: translateX(-100%);
    }
  }

  /* for Chrome */
  @-webkit-keyframes translate-x-animation {
    from {
      -webkit-transform: translateX(100%);
    }
    to {
      -webkit-transform: translateX(-100%);
    }
  }

  -moz-transform: translateX(100%);
  -webkit-transform: translateX(100%);
  transform: translateX(100%);

  -moz-animation: translate-x-animation 13s linear infinite;
  -webkit-animation: translate-x-animation 13s linear infinite;
  animation: translate-x-animation 13s linear infinite;
`;

const NotificationBadge = styled.span`
  position: absolute;
  right: 1px;
  top: 28px;
  width: 8px;
  height: 8px;
  background: ${(props: { color?: string }) =>
    props.color ? props.color : "red"};
  text-align: center;
  border-radius: 50%;
  color: white;
`;

const StatusTootTip = styled(Tooltip)`
  visibility: ${(props: any) => (props.open ? "visible" : "hidden")};
  margin-left: ${(props: any) => {
    const offset =
      props.options?.maxDigits === undefined || props.options.maxDigits < 7
        ? 0.6
        : props.options.maxDigits >= 7 && props.options.maxDigits < 16
        ? 0.55
        : 0.53;
    return props.openDirection?.includes("left")
      ? `-${props.width * offset}px !important`
      : `${props.width * offset}px !important`;
  }};
  z-index: ${(props: any) => (props.open ? "1000" : "-1")};
  margin-top: ${(props: any) => (props.top ? `-${props.top}px` : "0px")};
`;

function AlternatingLabel({
  messages,
  LabelComponent,
}: {
  messages: React.ReactNode[];
  LabelComponent: React.ComponentType<{ children: React.ReactNode }>;
}): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (messages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, ALTERNATING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [messages.length]);

  return <LabelComponent>{messages[currentIndex]}</LabelComponent>;
}

type Props = {
  offset?: any;
  label?: React.ReactNode;
  title?: string | null;
  disabled?: boolean;
  selected: boolean;
  options: {
    labelColor?: string;
    labelLength?: { length: number; nbSymbols: number };
    withLabel?: boolean;
    withLabelBackground?: boolean;
    tooltipCoordinates?: [];
    display_id?: string;
    maxDigits?: number;
    scrolling?: boolean;
    alternatingMessages?: React.ReactNode[] | null;
  };
  position: [number, number];
  onClick?: Function;
  statusColor?: string;
  statusHelper?: string;
};

function MapDisplay({
  disabled,
  title,
  offset,
  label,
  onClick,
  selected,
  position,
  options,
  statusColor,
  statusHelper,
}: Props): JSX.Element {
  const [tootTipOpen, setToolTipOpen] = useState(false);
  const ref = useRef<Tooltip>(null);
  const refStatus = useRef<Tooltip>(null);
  const LabelComponent = useMemo(() => {
    if (options?.scrolling) {
      return AutoScrollingLabel;
    }

    return StyledLabel;
  }, [options?.scrolling]);

  const color = useMemo(() => {
    return options?.labelColor || MapConfig?.iot?.display?.color?.default;
  }, [options?.labelColor]);

  // Determine if we should use alternating display
  const alternatingMessages = options?.alternatingMessages;
  const hasAlternating = alternatingMessages && alternatingMessages.length > 1;

  function handleOnClick() {
    if (disabled) return null;

    return onClick && onClick();
  }

  return (
    <>
      <CircleMarker
        interactive={!!onClick}
        center={position}
        radius={0}
        fillOpacity={0}
        opacity={0}
        onClick={handleOnClick}
      >
        <StyledTooltip
          selected={selected}
          interactive={!!onClick}
          direction="center"
          opacity={1}
          permanent
          offset={offset}
          color={color}
          maxDigits={options?.maxDigits || 0}
          labelLength={options?.labelLength}
          ref={ref}
        >
          {/* @ts-ignore */}
          {title ? (
            <div id="title" title={title}>
              {title}
            </div>
          ) : null}
          <LabelContainer>
            {hasAlternating ? (
              <AlternatingLabel
                messages={alternatingMessages}
                LabelComponent={LabelComponent}
              />
            ) : (
              <LabelComponent>{label}</LabelComponent>
            )}
          </LabelContainer>
          {statusColor && (
            <NotificationBadge
              onMouseEnter={(e) => {
                setToolTipOpen(true);
              }}
              onMouseLeave={(e) => {
                setToolTipOpen(false);
              }}
              color={statusColor}
            />
          )}
        </StyledTooltip>
      </CircleMarker>
      <CircleMarker
        interactive={false}
        center={position}
        radius={0}
        fillOpacity={0}
        opacity={0}
      >
        <StatusTootTip
          permanent
          // @ts-ignore
          open={tootTipOpen}
          // @ts-ignore
          width={ref.current?.leafletElement._container.offsetWidth}
          openDirection={
            // @ts-ignore
            refStatus.current?.leafletElement._container.className
          }
          top={offset[1]}
          ref={refStatus}
          options={options}
        >
          {statusHelper}
        </StatusTootTip>
      </CircleMarker>
    </>
  );
}

MapDisplay.defaultProps = {
  offset: [0, 0],
  selected: false,
};

export default MapDisplay;
